// ============================================================
// velo.js — EL VELO DE LEGIBILIDAD de una sección: una sola regla
//
// El velo es el degradado que va ENCIMA de la foto de fondo (y del fondo animado) y DEBAJO del
// texto, para que el titular se lea. Lo pinta el `::after` de la sección
// (`css/site.css::.unsplash-bg-target::after`, `background: var(--bg-overlay, none)`, z-index 1),
// y este módulo decide qué degradado lleva esa variable.
//
// Antes lo decidían TRES copias y no coincidían:
//   · `editor/unsplash.js` (la foto): el `bg_gradient` o, sin él, uno hecho con `data-bg-color`;
//   · `animations/backgrounds.js::injectGradientLayer` (con fondo animado): SOLO el
//     `bg_gradient`, metido en una capa `.bg-gradient-layer` dentro del fondo, porque una regla de
//     `site.css` APAGABA el `::after` en cuanto había fondo animado. Un hero con foto y sin
//     `bg_gradient` (una foto puesta a mano en el editor) perdía el velo entero: titular de
//     12-13:1 a 1,9-3,9:1 (L3 del plan del motor de fondos, medido en las tres superficies);
//   · `editor/gradient-panel.js` (la vista previa del panel), que además lo pintaba DOS veces.
// Desde el P2 del motor de fondos el fondo animado va por debajo del velo (contrato de capas de
// `site.css`), así que el velo es siempre el `::after` y esta es la única regla:
//
//   · se pinta si la sección tiene FOTO de fondo o FONDO ANIMADO (sin ninguno de los dos, no:
//     así las webs que no llevan ni lo uno ni lo otro se quedan exactamente como estaban);
//   · lleva el `bg_gradient` del bloque si lo hay; si no, y hay foto, el de respaldo hecho con
//     `data-bg-color` (90 % → 60 %); si no hay ni lo uno ni lo otro, no lleva velo. Solo el hero
//     emite `data-bg-color`: el banner v2 trae su propio velo (`.banner-overlay`) y el de color,
//     con un `bgColor` claro, le ponía un blanco encima de su texto blanco.
//
// `degradadoDelVelo` es PURA (node-testeable). `veloDeSeccion` y `aplicarVelo` leen y escriben
// el elemento. Consumidores: editor/unsplash.js, animations/fondos/motor.js (el runtime y la vista
// previa del BG Studio, que desde el P3 la pinta el motor) y editor/gradient-panel.js (vista previa).
// ES2019 sin `?.` ni `??` (lo importan los tests en node 12).
// ============================================================

import { readGradientFromAttr } from './gradient.js';

/**
 * El degradado CSS del velo, o '' si la sección no lleva velo.
 * @param {{hayFoto?: boolean, hayFondo?: boolean, gradienteCss?: string, colorFondo?: string}} o
 */
export function degradadoDelVelo(o) {
    const d = o || {};
    if (!d.hayFoto && !d.hayFondo) return '';
    if (d.gradienteCss) return d.gradienteCss;
    if (d.hayFoto && d.colorFondo) return 'linear-gradient(' + d.colorFondo + 'e6, ' + d.colorFondo + '99)';
    return '';
}

function _tieneFondoAnimado(el) {
    const a = el.getAttribute('data-bg-anim');
    return !!a && a !== 'none';
}

/**
 * El velo de ESTE elemento, leído de sus atributos (`data-unsplash-bg`, `data-bg-gradient`,
 * `data-bg-color`, `data-bg-anim`).
 * @param {HTMLElement} el
 * @param {{hayFondo?: boolean}} [opciones] - `hayFondo` manda sobre `data-bg-anim`: la vista previa
 *   del BG Studio pinta un fondo que todavía no está en los atributos, y el runtime solo lo afirma
 *   cuando de verdad crea la capa (con «reducir movimiento» se salta los fondos canvas).
 */
export function veloDeSeccion(el, opciones) {
    if (!el || typeof el.getAttribute !== 'function') return '';
    const hayFondo = (opciones && opciones.hayFondo !== undefined)
        ? !!opciones.hayFondo
        : _tieneFondoAnimado(el);
    return degradadoDelVelo({
        // Con espacios no es una foto (mismo criterio que `fondos/capacidad.js` y `_tiene_foto`).
        hayFoto: !!(el.getAttribute('data-unsplash-bg') || '').trim(),
        hayFondo: hayFondo,
        gradienteCss: readGradientFromAttr(el, '180deg'),
        colorFondo: el.getAttribute('data-bg-color') || '',
    });
}

/** Escribe (o quita) `--bg-overlay` en el elemento según `veloDeSeccion`. */
export function aplicarVelo(el, opciones) {
    if (!el || !el.style) return;
    const velo = veloDeSeccion(el, opciones);
    if (velo) el.style.setProperty('--bg-overlay', velo);
    else el.style.removeProperty('--bg-overlay');
}
